import type { ReactNode, Ref } from 'react';

interface Tab<T extends string> {
  id: T;
  label: ReactNode;
}

interface TabBarProps<T extends string> {
  tabs: Tab<T>[];
  activeTab: string;
  onChange: (id: T) => void;
  /** #647 — the test id of each tab is `${testId}-${tab.id}`. */
  testId?: string;
  /** #661 (F86) — `page` is the tab bar of a page (a small label on a phone,
   *  14 px from `sm`). `compact` is a strip inside a card or a panel modal
   *  (12 px, a softer border). */
  size?: 'page' | 'compact';
  /** More classes for the strip, for example `shrink-0` in a flex column. */
  className?: string;
  /** The strip itself: a page that scrolls the active tab into view reads
   *  `[aria-current="true"]` in it, so `aria-current` is a contract. */
  barRef?: Ref<HTMLDivElement>;
  id?: string;
}

const SIZE = {
  page: { bar: 'border-gray-200', tab: 'px-2 sm:px-4 py-2.5 text-[11px] sm:text-sm' },
  compact: { bar: 'border-gray-100', tab: 'px-3 py-2.5 text-xs' },
} as const;

/**
 * The underline tab bar. Every bar of the app draws it: the active tab has
 * `aria-current`, so a screen reader names it, and a spec finds it by the
 * class `border-gray-900` (`switchTab()`).
 */
export default function TabBar<T extends string = string>({ tabs, activeTab, onChange, testId, size = 'page', className = '', barRef, id }: TabBarProps<T>) {
  const s = SIZE[size];
  return (
    <div id={id} ref={barRef} className={`flex border-b ${s.bar} overflow-x-auto overflow-y-hidden no-scrollbar ${className}`.trim()}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          data-testid={testId ? `${testId}-${tab.id}` : undefined}
          aria-current={activeTab === tab.id ? 'true' : undefined}
          onClick={() => onChange(tab.id)}
          className={`${s.tab} font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 flex items-center gap-2 ${
            activeTab === tab.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
